import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Compass, TrendUp, Sparkle, X, CaretRight, Diamond } from "@phosphor-icons/react";
import GuideHelpButton from "../components/GuideHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_META = {
  emerging: { label: "Emerging", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", bar: "#10b981" },
  competitive: { label: "Competitive", cls: "bg-amber-50 text-amber-700 ring-amber-200", bar: "#f59e0b" },
  saturated: { label: "Saturated", cls: "bg-red-50 text-red-600 ring-red-200", bar: "#ef4444" },
  whitespace: { label: "💎 Whitespace", cls: "bg-violet-50 text-violet-700 ring-violet-200", bar: "#8b5cf6" },
  unvalidated: { label: "Unvalidated", cls: "bg-zinc-100 text-zinc-500 ring-zinc-200", bar: "#a1a1aa" },
};

const fmtVol = (v) => (v == null ? "—" : v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${v}`);
const fmtGrowth = (g) => {
  if (g == null) return null;
  const a = Math.abs(g), s = g < 0 ? "−" : "+";
  if (a >= 9900) return `${s}99×+`;
  if (a >= 1000) return `${s}${Math.round(a / 100)}×`;
  return `${s}${Math.round(a)}%`;
};

const SORTS = [
  { key: "volume", label: "Biggest demand" },
  { key: "whitespace", label: "💎 Most whitespace" },
  { key: "growth", label: "Fastest growing" },
  { key: "members", label: "Most topics" },
];

const VerdictBar = ({ dist, total }) => {
  const order = ["whitespace", "emerging", "competitive", "saturated", "unvalidated"];
  return (
    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-zinc-100">
      {order.map((v) => {
        const n = dist[v] || 0;
        if (!n) return null;
        return <div key={v} style={{ width: `${(n / total) * 100}%`, background: VERDICT_META[v].bar }} title={`${VERDICT_META[v].label}: ${n}`} />;
      })}
    </div>
  );
};

const TrendCard = ({ t, onOpen }) => {
  const growth = fmtGrowth(t.median_growth);
  return (
    <button
      onClick={() => onOpen(t)}
      data-testid={`meta-trend-card-${t.trend_id}`}
      className="text-left bg-white border border-zinc-200 rounded-2xl p-5 hover:border-violet-300 hover:shadow-[0_4px_24px_-12px_rgba(139,92,246,0.4)] transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-mono-data text-zinc-400">#{t.rank}</span>
          <h3 className="font-bold text-zinc-900 leading-tight truncate group-hover:text-violet-700 transition-colors">{t.label}</h3>
        </div>
        {t.n_whitespace > 0 && (
          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200" data-testid={`meta-whitespace-${t.trend_id}`}>
            <Diamond size={9} weight="fill" /> {t.n_whitespace}
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 leading-relaxed line-clamp-3 mb-3">{t.summary}</p>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {t.top_topics.slice(0, 3).map((tp) => (
          <span key={tp.slug} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 capitalize truncate max-w-[120px]">{tp.name}</span>
        ))}
        {t.member_count > 3 && <span className="text-[10px] px-1.5 py-0.5 text-zinc-400">+{t.member_count - 3} more</span>}
      </div>

      <VerdictBar dist={t.verdict_dist} total={t.member_count} />

      <div className="flex items-center justify-between mt-3 text-[11px]">
        <span className="text-zinc-500"><b className="text-zinc-800">{t.member_count}</b> topics</span>
        <span className="text-zinc-500"><b className="text-zinc-800">{fmtVol(t.total_search_volume)}</b> searches/mo</span>
        {growth && <span className="font-bold tabular-nums text-emerald-600 inline-flex items-center gap-0.5"><TrendUp size={11} weight="bold" />{growth}</span>}
      </div>
    </button>
  );
};

const DetailDrawer = ({ trend, onClose }) => {
  const [data, setData] = useState(null);
  const [verdict, setVerdict] = useState("all");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const PAGE = 30;

  useEffect(() => {
    setItems([]); setPage(1);
    axios.get(`${API}/trending/meta-trends/${trend.trend_id}`, { params: { verdict: verdict === "all" ? undefined : verdict, page: 1, page_size: PAGE } })
      .then((r) => { setData(r.data); setItems(r.data.topics || []); })
      .catch(() => setData({ total: 0, topics: [] }));
  }, [trend.trend_id, verdict]);

  const more = () => {
    const nx = page + 1;
    axios.get(`${API}/trending/meta-trends/${trend.trend_id}`, { params: { verdict: verdict === "all" ? undefined : verdict, page: nx, page_size: PAGE } })
      .then((r) => { setItems((p) => [...p, ...(r.data.topics || [])]); setPage(nx); });
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="meta-trend-drawer">
      <div className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white/90 backdrop-blur border-b border-zinc-100 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-widest text-violet-600 mb-0.5">META TREND · #{trend.rank}</div>
            <h2 className="text-xl font-extrabold text-zinc-900 leading-tight">{trend.label}</h2>
          </div>
          <button onClick={onClose} data-testid="meta-drawer-close" className="flex-shrink-0 p-2 text-zinc-400 hover:text-zinc-900 rounded-full hover:bg-zinc-100"><X size={20} /></button>
        </div>

        <div className="px-5 py-4">
          <p className="text-sm text-zinc-600 leading-relaxed mb-4">{trend.summary}</p>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3 text-center">
              <div className="text-lg font-extrabold text-zinc-900">{trend.member_count}</div>
              <div className="text-[10px] text-zinc-500">topics</div>
            </div>
            <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3 text-center">
              <div className="text-lg font-extrabold text-zinc-900">{fmtVol(trend.total_search_volume)}</div>
              <div className="text-[10px] text-zinc-500">searches/mo</div>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-3 text-center">
              <div className="text-lg font-extrabold text-violet-700">{trend.n_whitespace}</div>
              <div className="text-[10px] text-violet-500">💎 whitespace</div>
            </div>
          </div>

          {(trend.top_industries || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {trend.top_industries.map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{c}</span>)}
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 mb-3">
            {["all", "whitespace", "emerging", "competitive", "saturated", "unvalidated"].map((v) => (
              <button key={v} onClick={() => setVerdict(v)} data-testid={`meta-verdict-filter-${v}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${verdict === v ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>
                {v === "all" ? "All topics" : (VERDICT_META[v]?.label || v)}
              </button>
            ))}
          </div>

          <div className="text-xs text-zinc-400 mb-2">{data ? `${data.total} topics in this movement` : "Loading…"}</div>
          <div className="space-y-1.5">
            {items.map((tp) => {
              const vm = VERDICT_META[tp.verdict] || VERDICT_META.unvalidated;
              const g = fmtGrowth(tp.growth);
              return (
                <div key={tp.slug} className="flex items-center gap-3 p-2.5 rounded-xl bg-zinc-50 border border-zinc-100" data-testid={`meta-topic-${tp.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-800 capitalize truncate">{tp.name}</div>
                    <span className={`inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ring-1 ${vm.cls}`}>{vm.label}</span>
                  </div>
                  {g && <div className="text-xs font-bold tabular-nums text-emerald-600 flex-shrink-0">{g}</div>}
                  <div className="text-right flex-shrink-0 w-14">
                    <div className="text-xs font-bold text-zinc-900">{fmtVol(tp.search_volume)}</div>
                    <div className="text-[9px] text-zinc-400">/mo</div>
                  </div>
                </div>
              );
            })}
          </div>

          {data && items.length < data.total && (
            <div className="flex justify-center mt-4">
              <button onClick={more} data-testid="meta-drawer-load-more" className="px-5 py-2 rounded-full border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-violet-300">
                Load more · {(data.total - items.length).toLocaleString()} more
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function MetaTrends() {
  const [trends, setTrends] = useState([]);
  const [totals, setTotals] = useState({ trends: 0, topics: 0, whitespace: 0 });
  const [sort, setSort] = useState("volume");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/trending/meta-trends`, { params: { sort } })
      .then((r) => { setTrends(r.data.trends || []); setTotals(r.data.totals || {}); })
      .finally(() => setLoading(false));
  }, [sort]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="meta-trends-view">
      <div className="text-xs font-bold tracking-widest text-violet-600 mb-2 inline-flex items-center gap-1.5"><Compass size={13} weight="fill" /> TRENDING · META TRENDS</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2 flex items-center gap-2">
        The movements behind the noise.
        <GuideHelpButton feature="meta" />
      </h1>
      <p className="text-zinc-500 max-w-3xl text-sm">
        We grouped <b>{totals.topics?.toLocaleString()}</b> individual demand topics into <b>{totals.trends}</b> larger <b className="text-violet-700">meta trends</b> — the big-picture movements founders actually build into. Each is grounded in real search volume + competition verdicts, with <b className="text-violet-700">{totals.whitespace?.toLocaleString()} 💎 whitespace</b> topics flagged inside. Open one to see every topic in the movement.
      </p>

      <div className="mt-5 mb-5 flex flex-wrap gap-2" data-testid="meta-sort-pills">
        {SORTS.map((s) => (
          <button key={s.key} onClick={() => setSort(s.key)} data-testid={`meta-sort-${s.key}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${sort === s.key ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading && trends.length === 0 ? (
        <div className="text-zinc-400 text-sm py-16 text-center">Grouping topics into movements…</div>
      ) : trends.length === 0 ? (
        <div className="text-zinc-500 text-sm py-16 text-center">
          <Sparkle size={28} className="mx-auto text-zinc-300 mb-2" weight="duotone" />
          Meta trends are still building — check back in a couple of minutes.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trends.map((t) => <TrendCard key={t.trend_id} t={t} onOpen={setSelected} />)}
        </div>
      )}

      {selected && <DetailDrawer trend={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
